import { Col, Row } from "antd";
import { useTranslation } from "next-i18next";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { useRouter } from "next/router";
import Image from "next/image";
import { I18Text } from "src/types/i18Text";
import { down } from "styled-breakpoints";
import styled from "styled-components";
import {
  Hero as SundayHero,
  Headline as SundayHeadline,
  Body as SundayBody,
  Title as SundayTitle,
} from "sunday-component";
import { useState } from "react";
import Button from "../Common/Button";
import ArrowRightIcon from "../Common/Icons/ArrowRightIcon";

const Container = styled.div<{
  bgColor: string;
}>`
  background-color: ${(props) => props.bgColor};
`;

const ContentContainer = styled.div<{
  isFullSize: boolean;
}>`
  max-width: 938px;
  margin: auto;
  padding: ${(props) =>
    props.isFullSize ? "80px 16px 80px 16px" : "80px 16px 0px 16px"};
  ${down("lg")} {
    padding: ${(props) =>
      props.isFullSize ? "80px 16px 80px 16px" : "80px 16px 0px 16px"};
  }
`;

const TitleDesktop = styled(SundayHero)<{
  titleColor: string;
}>`
  color: ${(props) => props.titleColor};
  text-align: start;
  margin-bottom: 16px;
`;

const TitleMobile = styled(SundayHeadline)<{
  titleColor: string;
}>`
  color: ${(props) => props.titleColor};
  text-align: center;
  margin-bottom: 16px;
`;

const DescriptionDesktop = styled(SundayBody)<{
  contentColor: string;
}>`
  color: ${(props) => props.contentColor};
  text-align: start;
  margin-bottom: 40px;
`;

const DescriptionMobile = styled(SundayBody)<{
  contentColor: string;
}>`
  color: ${(props) => props.contentColor};
  text-align: center;
  margin-bottom: 40px;
`;

const ButtonText = styled(SundayTitle)``;

const ButtonContainer = styled.div`
  width: 330px;

  ${down("md")} {
    width: 100%;
  }
`;

const ArrowLogoContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
`;

// DEV-NOTE: use position relative for Image layout=fill.
// ref: https://nextjs.org/docs/api-reference/next/image#fill
const BannerImageLogoContainer = styled.div`
  height: 256px;
  position: relative;
  ${down("md")} {
    height: 178px;
    margin-bottom: 40px;
  }
`;

export type OtherPlanBannerProps = {
  imageUrl: string;
  title: I18Text;
  description: I18Text;
  bgColor: string;
  isFullSize?: boolean;
  onSeeOtherPlans: Function;
  titleColor: string;
  contentColor: string;
};

const OtherPlanBanner = ({
  imageUrl,
  title,
  description,
  bgColor,
  isFullSize,
  onSeeOtherPlans = () => {},
  titleColor,
  contentColor,
}: OtherPlanBannerProps) => {
  const { locale } = useRouter();
  const { lg } = useBreakpoint();
  const { t } = useTranslation("plan-content-direct-link");

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSeeOtherPlans = async () => {
    setIsLoading(true);
    await onSeeOtherPlans();
    setIsLoading(false);
  };

  return (
    <Container bgColor={bgColor}>
      <ContentContainer isFullSize={isFullSize}>
        <Row align="bottom">
          <Col xl={12} lg={12} md={24} sm={24} xs={24} order={lg ? 1 : 2}>
            {lg ? (
              <>
                <TitleDesktop level={4} titleColor={titleColor}>
                  {title[locale]}
                </TitleDesktop>
                <DescriptionDesktop level={5} contentColor={contentColor}>
                  {description[locale]}
                </DescriptionDesktop>
                <ButtonContainer>
                  <Button
                    data-testid="see-other-plan-button"
                    data-gtm-input-code="see-other-plan-button"
                    fullWidth
                    onClick={handleSeeOtherPlans}
                    loading={isLoading}
                  >
                    <ButtonText level={7}>
                      {t("otherPlanBanner.button")}{" "}
                      <ArrowLogoContainer>
                        <ArrowRightIcon width={24} height={24} />
                      </ArrowLogoContainer>
                    </ButtonText>
                  </Button>
                </ButtonContainer>
              </>
            ) : (
              <>
                <TitleMobile level={3} titleColor={titleColor}>
                  {title[locale]}
                </TitleMobile>
                <DescriptionMobile level={7} contentColor={contentColor}>
                  {description[locale]}
                </DescriptionMobile>
                <ButtonContainer>
                  <Button
                    data-testid="see-other-plan-button"
                    data-gtm-input-code="see-other-plan-button"
                    fullWidth
                    onClick={onSeeOtherPlans}
                    loading={isLoading}
                  >
                    <ButtonText level={7}>
                      {t("otherPlanBanner.button")}{" "}
                      <ArrowLogoContainer>
                        <ArrowRightIcon width={24} height={24} />
                      </ArrowLogoContainer>
                    </ButtonText>
                  </Button>
                </ButtonContainer>
              </>
            )}
          </Col>
          <Col xl={12} lg={12} md={24} sm={24} xs={24} order={lg ? 2 : 1}>
            <BannerImageLogoContainer>
              <Image src={imageUrl} layout="fill" />
            </BannerImageLogoContainer>
          </Col>
        </Row>
      </ContentContainer>
    </Container>
  );
};

export default OtherPlanBanner;
